import { FC } from 'react';
import FlatButton from '@/components/flat-button';
import useUserSettings from '@/hooks/use-user-settings.hook';
import { createUseStyles } from 'react-jss';
import Tooltip from '@/components/tooltip';
import { addAction } from '@/lib/datadog';
import useThread from '@/hooks/use-thread.hook';
import { Stack } from '@mui/material';
import Icon from '@/components/icon';
import classNames from 'classnames';

interface Props {
  onChange: (enabled: boolean) => void;
}

const useStyles = createUseStyles({
  searchButton: {
    textWrap: 'nowrap',
    cursor: 'pointer',
    '&.disabled': {
      cursor: 'default',
    },
  },
});

const PublicInternetSearchButton: FC<Props> = ({ onChange }) => {
  const styles = useStyles();
  const { publicSearchAvailable, defaultAgent } = useUserSettings();
  const {
    thread,
    settings: { publicSearchEnabled, isDefaultAgent },
    togglePublicSearch,
  } = useThread();

  const handleToggleSearch = () => {
    if (disabled) {
      return;
    }
    const updated = !publicSearchEnabled;
    addAction('public_search_toggle', { enabled: updated, threadId: thread.id });
    togglePublicSearch(updated);
    onChange(updated);
  };

  if (!publicSearchAvailable) {
    return null;
  }

  const disabled = !isDefaultAgent;
  const compClass = classNames(styles.searchButton, { disabled });

  return (
    <Tooltip
      title={`You can only use Public Internet Search when the ${defaultAgent.badgeTitle} assistant is selected for the chat.`}
      enterDelay={500}
      disabled={!disabled}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-start"
        gap={0.5}
        className={compClass}
        onClick={handleToggleSearch}
        role="button"
      >
        <FlatButton
          icon="globe"
          label="Public Internet Search"
          size="small"
          color={publicSearchEnabled ? 'link' : undefined}
          disabled={disabled}
          responsive={false}
        />
        {publicSearchEnabled && <Icon name="close" size="x-small" color="link" />}
      </Stack>
    </Tooltip>
  );
};

export default PublicInternetSearchButton;
